import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { Project } from '../types/types';
import { fetchProjects } from '../services/portfolioService';

/**
 * Interactive drag canvas: "me" at the center, real projects radiating out
 * (fetched from the backend, not hardcoded), and each project's own real
 * technologies radiating from it in turn. Nodes are draggable; edges are
 * thin stone hairlines recomputed live from plain React state — no physics
 * library, just clamped pointer-delta math.
 */

const STONE = '#CBC6B7';

type NodeType = 'me' | 'project' | 'tech';

interface GraphNode {
  id: string;
  label: string;
  type: NodeType;
  angle: number;
  radiusFrac: number;
}

interface Edge {
  from: string;
  to: string;
}

interface Point {
  x: number;
  y: number;
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function circularMean(angles: number[]): number {
  const sin = angles.reduce((s, a) => s + Math.sin(a), 0);
  const cos = angles.reduce((s, a) => s + Math.cos(a), 0);
  return Math.atan2(sin, cos);
}

function buildGraph(projects: Project[]): { nodes: GraphNode[]; edges: Edge[] } {
  const nodes: GraphNode[] = [{ id: 'me', label: 'JP', type: 'me', angle: 0, radiusFrac: 0 }];
  const edges: Edge[] = [];
  if (projects.length === 0) return { nodes, edges };

  const projectAngle = new Map<string, number>();
  projects.forEach((p, i) => {
    const angle = -Math.PI / 2 + i * ((2 * Math.PI) / projects.length);
    const id = `project-${p.id}`;
    projectAngle.set(id, angle);
    nodes.push({ id, label: p.title, type: 'project', angle, radiusFrac: 0.42 });
    edges.push({ from: 'me', to: id });
  });

  const techParents = new Map<string, string[]>();
  const techLabel = new Map<string, string>();
  projects.forEach(p => {
    const projId = `project-${p.id}`;
    const seen = new Set<string>();
    p.technologies
      .split(/,\s*/)
      .map(t => t.trim())
      .filter(Boolean)
      .forEach(t => {
        const key = t.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        techLabel.set(key, t);
        const parents = techParents.get(key) || [];
        parents.push(projId);
        techParents.set(key, parents);
      });
  });

  let techIndex = 0;
  techParents.forEach((parents, key) => {
    const id = `tech-${key.replace(/[^a-z0-9]+/g, '-')}`;
    const baseAngle = circularMean(parents.map(pid => projectAngle.get(pid) ?? 0));
    const jitter = ((hashStr(id) % 31) - 15) * (Math.PI / 180);
    // Two staggered sub-rings (not one narrow band) so there's real 2D room
    // for the relax pass to spread a dense tech list into.
    const ring = techIndex % 2 === 0 ? 0.56 : 0.88;
    const radiusJitter = ((hashStr(id + 'r') % 13) - 6) / 100;
    nodes.push({ id, label: techLabel.get(key) || key, type: 'tech', angle: baseAngle + jitter, radiusFrac: ring + radiusJitter });
    parents.forEach(pid => edges.push({ from: pid, to: id }));
    techIndex += 1;
  });

  return { nodes, edges };
}

function restingPosition(node: GraphNode, cx: number, cy: number, halfMin: number): Point {
  if (node.type === 'me') return { x: cx, y: cy };
  const r = node.radiusFrac * halfMin;
  return { x: cx + r * Math.cos(node.angle), y: cy + r * Math.sin(node.angle) };
}

const DEFAULT_HALF = { me: 26, project: 60, tech: 44 };
// Safety margin so a chip's own border never sits flush against (or past)
// the canvas edge — covers any box-model rounding, not just aesthetics.
const EDGE_INSET = 6;

/**
 * Lightweight overlap relaxation — not a physics engine, just repeated
 * pairwise pushes so chips separate when initial radial placement made two
 * of them collide. "me" never moves; everything else nudges apart and stays
 * clamped inside the canvas.
 */
function relax(
  nodeIds: string[],
  start: Map<string, Point>,
  sizeOf: (id: string) => { hw: number; hh: number },
  width: number,
  height: number
): Map<string, Point> {
  const pts = new Map<string, Point>();
  start.forEach((p, id) => pts.set(id, { x: p.x, y: p.y }));
  const PADDING = 5;
  const clamp = (p: Point, s: { hw: number; hh: number }) => {
    p.x = Math.min(Math.max(p.x, s.hw + EDGE_INSET), width - s.hw - EDGE_INSET);
    p.y = Math.min(Math.max(p.y, s.hh + EDGE_INSET), height - s.hh - EDGE_INSET);
  };

  for (let iter = 0; iter < 160; iter++) {
    let moved = false;
    for (let i = 0; i < nodeIds.length; i++) {
      for (let j = i + 1; j < nodeIds.length; j++) {
        const idA = nodeIds[i];
        const idB = nodeIds[j];
        const a = pts.get(idA)!;
        const b = pts.get(idB)!;
        const sa = sizeOf(idA);
        const sb = sizeOf(idB);
        const dx = b.x - a.x || 0.01;
        const dy = b.y - a.y || 0.01;
        const overlapX = sa.hw + sb.hw + PADDING - Math.abs(dx);
        const overlapY = sa.hh + sb.hh + PADDING - Math.abs(dy);
        if (overlapX > 0 && overlapY > 0) {
          const overlap = Math.min(overlapX, overlapY);
          const dist = Math.hypot(dx, dy) || 0.01;
          const pushX = (dx / dist) * overlap * 0.5;
          const pushY = (dy / dist) * overlap * 0.5;
          const aFixed = idA === 'me';
          const bFixed = idB === 'me';
          if (!aFixed) {
            a.x -= pushX;
            a.y -= pushY;
            clamp(a, sa);
          }
          if (!bFixed) {
            b.x += pushX;
            b.y += pushY;
            clamp(b, sb);
          }
          moved = true;
        }
      }
    }
    if (!moved) break;
  }

  // Guaranteed final pass: nothing may sit on top of the fixed "me" node,
  // regardless of how the iterative pairwise pushes above settled.
  const me = pts.get('me');
  const meSize = sizeOf('me');
  if (me) {
    nodeIds.forEach(id => {
      if (id === 'me') return;
      const p = pts.get(id)!;
      const s = sizeOf(id);
      const minDist = meSize.hw + s.hw + 10;
      const dx = p.x - me.x || 0.01;
      const dy = p.y - me.y || 0.01;
      const dist = Math.hypot(dx, dy);
      if (dist < minDist) {
        const scale = minDist / dist;
        p.x = me.x + dx * scale;
        p.y = me.y + dy * scale;
        clamp(p, s);
      }
    });
  }

  return pts;
}

const WorkGraph: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const sizeRef = useRef<Map<string, { hw: number; hh: number }>>(new Map());
  const dragRef = useRef<{ id: string; grabDX: number; grabDY: number } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [positions, setPositions] = useState<Map<string, Point>>(new Map());
  const reduce = useReducedMotion();

  useEffect(() => {
    fetchProjects()
      .then(data => setProjects(data || []))
      .catch(() => setProjects([]));
  }, []);

  const { nodes, edges } = useMemo(() => buildGraph(projects), [projects]);
  const nodeTypeById = useMemo(() => new Map(nodes.map(n => [n.id, n.type])), [nodes]);

  // Measure the container once (and on resize) — real pixel space shared by
  // the HTML nodes and the SVG edge overlay.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Lay out nodes once the container has a real size, then clamp using each
  // node's actual measured chip size so long project titles never spill out.
  useLayoutEffect(() => {
    if (containerSize.width === 0 || nodes.length === 0) return;
    const cx = containerSize.width / 2;
    const cy = containerSize.height / 2;
    const halfMin = Math.min(containerSize.width, containerSize.height) / 2;

    const next = new Map<string, Point>();
    nodes.forEach(n => {
      const raw = restingPosition(n, cx, cy, halfMin);
      const measured = sizeRef.current.get(n.id);
      const hw = measured?.hw ?? DEFAULT_HALF[n.type];
      const hh = measured?.hh ?? DEFAULT_HALF[n.type];
      next.set(n.id, {
        x: Math.min(Math.max(raw.x, hw + EDGE_INSET), containerSize.width - hw - EDGE_INSET),
        y: Math.min(Math.max(raw.y, hh + EDGE_INSET), containerSize.height - hh - EDGE_INSET),
      });
    });
    setPositions(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerSize.width, containerSize.height, nodes]);

  // Measure real chip sizes after first paint, then relax overlaps using
  // those real sizes. Runs before the browser paints the entrance animation
  // (opacity 0 -> 1), so the rough-then-relaxed jump is never actually seen.
  useLayoutEffect(() => {
    if (containerSize.width === 0 || nodes.length === 0) return;
    nodes.forEach(n => {
      const el = nodeRefs.current.get(n.id);
      if (el) sizeRef.current.set(n.id, { hw: el.offsetWidth / 2, hh: el.offsetHeight / 2 });
    });
    const ids = nodes.map(n => n.id);
    const sizeOf = (id: string) => sizeRef.current.get(id) ?? { hw: DEFAULT_HALF.tech, hh: DEFAULT_HALF.tech };
    setPositions(prev => relax(ids, prev, sizeOf, containerSize.width, containerSize.height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, containerSize.width, containerSize.height]);

  const handlePointerDown = (id: string) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const pos = positions.get(id);
    if (!pos) return;
    dragRef.current = {
      id,
      grabDX: e.clientX - rect.left - pos.x,
      grabDY: e.clientY - rect.top - pos.y,
    };
    setDraggingId(id);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const container = containerRef.current;
    if (!drag || !container) return;
    const rect = container.getBoundingClientRect();
    const size = sizeRef.current.get(drag.id);
    const type = nodeTypeById.get(drag.id) ?? 'tech';
    const hw = size?.hw ?? DEFAULT_HALF[type];
    const hh = size?.hh ?? DEFAULT_HALF[type];

    const rawX = e.clientX - rect.left - drag.grabDX;
    const rawY = e.clientY - rect.top - drag.grabDY;
    const x = Math.min(Math.max(rawX, hw + EDGE_INSET), rect.width - hw - EDGE_INSET);
    const y = Math.min(Math.max(rawY, hh + EDGE_INSET), rect.height - hh - EDGE_INSET);

    setPositions(prev => {
      const next = new Map(prev);
      next.set(drag.id, { x, y });
      return next;
    });
  };

  const endDrag = () => {
    dragRef.current = null;
    setDraggingId(null);
  };

  const label = nodes.length
    ? `Diagram connecting me to my projects (${projects.map(p => p.title).join(', ')}) and the technologies behind them. Full project details are in the Projects section below.`
    : 'Diagram of my projects and technologies, loading.';

  return (
    <div className="wg-wrapper">
      <div
        ref={containerRef}
        className="wg-canvas"
        role="img"
        aria-label={label}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <svg className="wg-edges" width={containerSize.width} height={containerSize.height} aria-hidden="true">
          {edges.map((edge, i) => {
            const a = positions.get(edge.from);
            const b = positions.get(edge.to);
            if (!a || !b) return null;
            return (
              <motion.line
                key={`${edge.from}-${edge.to}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={STONE}
                strokeWidth={1}
                initial={reduce ? false : { pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ delay: reduce ? 0 : 0.15 + i * 0.02, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              />
            );
          })}
        </svg>

        {nodes.map((n, i) => {
          const pos = positions.get(n.id);
          if (!pos) return null;
          return (
            <motion.div
              key={n.id}
              ref={el => {
                if (el) nodeRefs.current.set(n.id, el);
              }}
              className={`wg-node wg-node--${n.type}${draggingId === n.id ? ' wg-node--dragging' : ''}`}
              style={{ left: pos.x, top: pos.y, x: '-50%', y: '-50%' }}
              aria-hidden="true"
              onPointerDown={handlePointerDown(n.id)}
              initial={reduce ? false : { opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: reduce ? 0 : 0.05 + i * 0.025, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {n.label}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkGraph;
